import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  memo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Select } from "../ui/Select";
import { FileUploader } from "../ui/FileUploader";
import { Spinner } from "../ui/Spinner";
import { Alert } from "../ui/Alert";
import { Text } from "../ui/Text";
import { useApi } from "../../context/ApiContext";
import { useStore } from "../../context/StoreContext";
import SearchableDropdown from "../SearchableDropdown";
import { TranslatableField } from "../common/TranslatableField";
import { SupportedLanguage } from "../../types/translations.types";
import {
  ModalLanguageProvider,
  useModalLanguage,
} from "../../context/ModalLanguageContext";
import { LanguageSwitcher } from "../common/LanguageSwitcher";
import { MultiValueInput } from "../ui/MultiValueInput";
import { MultiSelect } from "../ui/MultiSelect";
import { VariantCombinationsTable } from "./VariantCombinationsTable";
import { AssetSelector } from "./AssetSelector";
import { GalleryImageSelector } from "./GalleryImageSelector";
import { FaPlus, FaTrash, FaInfoCircle } from "react-icons/fa";
import { COUNTRIES, getCountryByCode } from "../../utils/countryCurrencyData";

interface VariantOptionValue {
  value: string;
  addOnPrice: number;
  sku?: string;
  image?: string;
  inventoryQuantity?: number;
  isActive: boolean;
  translations?: { [languageCode: string]: string };
}

interface VariantOption {
  name: string;
  values: VariantOptionValue[];
  required: boolean;
  displayOrder: number;
  translations?: { [languageCode: string]: string };
}

interface VariantCombination {
  combination: string; // Format: "Size:Small|Color:Red"
  price: number;
  inventoryQuantity?: number;
  description?: string;
  imageIndex?: number;
  locationPricing?: LocationPricing[];
  digitalFiles?: Array<{
    key: string;
    filename: string;
    size?: number;
    contentType?: string;
    countryCodes?: string[]; // ISO 3166-1 alpha-2 country codes
  }>;
  translations?: { [languageCode: string]: string };
  // Shipping-related fields
  weight?: number;
  weightUnit?: "kg" | "g" | "lb" | "oz";
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit: "cm" | "in";
  };
  fragility?: "none" | "low" | "medium" | "high";
  requiresSpecialHandling?: boolean;
}

interface Metafield {
  key: string;
  value: string;
  namespace?: string;
}

type DiscountType = "amount" | "percentage";
type DiscountMode = "static" | "tiered";

interface DiscountTier {
  minQuantity: number;
  type?: DiscountType;
  value: number;
}

interface DiscountConfig {
  mode: DiscountMode;
  type: DiscountType;
  value: number;
  tiers?: DiscountTier[];
}

interface LocationPricing {
  locationId: string;
  price?: number;
  inventoryQuantity?: number;
  isAvailable: boolean;
  discount?: DiscountConfig;
}

interface Product {
  translations?: Record<string, Record<SupportedLanguage, string>>;
  _id?: string;
  title: string;
  handle?: string;
  description: string;
  status: "active" | "draft" | "archived";
  basePrice: number;
  compareAtPrice?: number;
  variantOptions: VariantOption[];
  variantCombinations?: VariantCombination[];
  // Shipping-related fields (product-level, used when no variants are configured)
  weight?: number;
  weightUnit?: "kg" | "g" | "lb" | "oz";
  dimensions?: {
    length: number;
    width: number;
    height: number;
    unit: "cm" | "in";
  };
  images: Array<{
    url: string;
    alt?: string;
    isPrimary?: boolean;
    translations?: { [languageCode: string]: string };
    variantCombinations?: string[];
    type?: "image" | "video";
  }>;
  categories?: string[];
  vendor?: string;
  productType?: string;
  tags: string[];
  seoTitle?: string;
  seoDescription?: string;
  giftCard: boolean;
  featured?: boolean;
  isCustomizable?: boolean;
  customizationType?: "2d-print" | "3d-print";
  canvasSize?: string; // Canvas size for 2D print customization
  backgroundImages?: string[]; // Background images for customization (deprecated)
  availableAssetIds?: string[]; // IDs of assets from the store's asset library
  allowedDesignerFonts?: string[]; // Allowed fonts for 2D designer for this product
  availableColors?: string[]; // Available colors for 3D customizable products (hex color codes)
  isDigital?: boolean; // Whether this is a digital product
  digitalFiles?: Array<{
    key: string;
    filename: string;
    size?: number;
    contentType?: string;
    countryCodes?: string[]; // ISO 3166-1 alpha-2 country codes
  }>;
  metafields: Metafield[];
  inventoryQuantity?: number;
  locationPricing?: LocationPricing[];
}

interface ProductFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  productId?: string;
  onSuccess: () => void;
}

interface LocationManagementSectionProps {
  formData: Partial<Product>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Product>>>;
  locationMaterialCosts: Record<string, number | undefined>;
  setLocationMaterialCosts: React.Dispatch<
    React.SetStateAction<Record<string, number | undefined>>
  >;
  rootMaterialCost?: number;
}

// Variant Options Manager Component
interface VariantOptionsManagerProps {
  variantOptions: VariantOption[];
  setFormData: React.Dispatch<React.SetStateAction<Partial<Product>>>;
  formData: Partial<Product>;
  t: (key: string) => string;
  removeVariantOption: (index: number) => void;
}

const VariantOptionsManager: React.FC<VariantOptionsManagerProps> = ({
  variantOptions,
  setFormData,
  t,
  removeVariantOption,
}) => {
  return (
    <div className="border border-gray-200 rounded overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-40">
              {t("admin.productForm.optionName")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
              {t("admin.productForm.values")}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-20">
              {/* Actions column */}
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {variantOptions.map((option, optionIndex) => (
            <tr key={optionIndex} className="hover:bg-gray-50">
              <td className="px-3 py-2 whitespace-nowrap">
                <Input
                  value={option.name}
                  onChange={(e) => {
                    setFormData((prev) => {
                      const newOptions = [...(prev.variantOptions || [])];
                      const oldName = newOptions[optionIndex].name;
                      newOptions[optionIndex].name = e.target.value;
                      // Clear variant combinations when option name changes
                      const updatedCombinations = (
                        prev.variantCombinations || []
                      ).filter((combo: any) => {
                        // Remove combinations that reference the old option name
                        const pairs = combo.combination.split("|");
                        return !pairs.some((pair: string) => {
                          const [optionName] = pair.split(":");
                          return optionName === oldName;
                        });
                      });
                      return {
                        ...prev,
                        variantOptions: newOptions,
                        variantCombinations: updatedCombinations,
                      };
                    });
                  }}
                  className="w-full"
                  label=""
                />
              </td>
              <td className="px-3 py-2">
                <MultiValueInput
                  values={option.values.map((v) => v.value)}
                  onChange={(newValues) => {
                    setFormData((prev) => {
                      const newOptions = [...(prev.variantOptions || [])];

                      // Remove values that are no longer in the list
                      newOptions[optionIndex].values = newOptions[
                        optionIndex
                      ].values.filter((v) => newValues.includes(v.value));
                      // Add new values
                      const existingValues = new Set(
                        newOptions[optionIndex].values.map((v) => v.value),
                      );
                      newValues.forEach((val) => {
                        if (!existingValues.has(val)) {
                          newOptions[optionIndex].values.push({
                            value: val,
                            addOnPrice: 0,
                            isActive: true,
                          });
                        }
                      });

                      // Clear variant combinations that reference removed values or changed option structure
                      const optionName = newOptions[optionIndex].name;
                      const updatedCombinations = (
                        prev.variantCombinations || []
                      ).filter((combo: any) => {
                        const pairs = combo.combination.split("|");
                        return pairs.every((pair: string) => {
                          const [optName, optValue] = pair.split(":");
                          // Keep combination if it doesn't reference this option, or if the value still exists
                          if (optName !== optionName) return true;
                          return newValues.includes(optValue);
                        });
                      });

                      return {
                        ...prev,
                        variantOptions: newOptions,
                        variantCombinations: updatedCombinations,
                      };
                    });
                  }}
                  placeholder={
                    t("admin.productForm.enterValues") ||
                    "Enter values (press space or comma to add)"
                  }
                  className="w-full"
                />
              </td>
              <td className="px-3 py-2 whitespace-nowrap">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    removeVariantOption(optionIndex);
                  }}
                >
                  {t("admin.remove")}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Country Management Section Component
const LocationManagementSection: React.FC<LocationManagementSectionProps> = ({
  formData,
  setFormData,
  locationMaterialCosts,
  setLocationMaterialCosts,
  rootMaterialCost,
}) => {
  const { api } = useApi();
  const { isPluginEnabled } = useStore();
  const isProfitPluginEnabled = isPluginEnabled("profit-rules");
  const { t } = useTranslation();
  const [locations, setLocations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true);
        const response = await api.get("/locations/admin?isActive=true");
        setLocations(response.data.locations || []);
      } catch (err) {
        console.error("Failed to fetch locations:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLocations();
  }, [api]);

  const updateLocationPricing = (
    locationId: string,
    updates: Partial<LocationPricing>,
  ) => {
    setFormData((prev) => {
      const locationPricing = [...(prev.locationPricing || [])];
      const existingIndex = locationPricing.findIndex(
        (lp) => lp.locationId === locationId,
      );

      if (existingIndex >= 0) {
        locationPricing[existingIndex] = {
          ...locationPricing[existingIndex],
          ...updates,
        };
      } else {
        locationPricing.push({
          locationId,
          isAvailable: true,
          ...updates,
        });
      }

      return { ...prev, locationPricing };
    });
  };

  const removeLocationPricing = (locationId: string) => {
    setFormData((prev) => ({
      ...prev,
      locationPricing: (prev.locationPricing || []).filter(
        (lp) => lp.locationId !== locationId,
      ),
    }));
  };

  // Check for inconsistent inventory configuration
  const hasInconsistentInventory = useMemo(() => {
    // Only check for base products (not variants)
    if (formData.variantOptions && formData.variantOptions.length > 0) {
      return false; // Variants manage inventory at variant level
    }
    const locationsWithInventory = (formData.locationPricing || []).filter(
      (lp) =>
        lp.inventoryQuantity !== undefined && lp.inventoryQuantity !== null,
    );
    // If some countries have inventory set but not all, it's inconsistent
    return (
      locationsWithInventory.length > 0 &&
      locationsWithInventory.length < locations.length
    );
  }, [formData.locationPricing, formData.variantOptions, locations.length]);

  if (loading) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {t("admin.productForm.countryManagement")}
        </h3>
        <div className="flex justify-center py-4">
          <Spinner size="sm" />
        </div>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">
          {t("admin.productForm.countryManagement")}
        </h3>
        <Text size="sm" color="muted" className="text-gray-600">
          {t("admin.productForm.noCountriesConfigured")}
        </Text>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {t("admin.productForm.countryManagement")}
        </h3>
        <Text size="sm" color="muted" className="text-gray-600">
          {t("admin.productForm.countryManagementDescription")}
        </Text>
      </div>
      {hasInconsistentInventory && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
          <svg
            className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <div className="flex-1">
            <p className="text-sm font-medium text-yellow-800">
              {t("admin.productForm.inconsistentInventoryTitle")}
            </p>
            <p className="text-xs text-yellow-700 mt-1">
              {t("admin.productForm.inconsistentInventoryMessage")}
            </p>
          </div>
        </div>
      )}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-48">
                  {t("admin.productForm.country")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider w-24">
                  {t("admin.productForm.available")}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                  {t("admin.productForm.price")}
                </th>
                {isProfitPluginEnabled && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    {t(
                      "admin.productProfitSettings.materialCosts.materialCost",
                    )}
                  </th>
                )}
                {!formData.isDigital && (
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    {t("admin.productForm.quantity")}
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {locations.map((location) => {
                const locationPricing = (formData.locationPricing || []).find(
                  (lp) => lp.locationId === location._id,
                );
                const isAvailable = locationPricing?.isAvailable ?? true;
                const locationPrice = locationPricing?.price;
                const locationQuantity = locationPricing?.inventoryQuantity;
                const locationMaterialCost =
                  locationMaterialCosts[location._id];

                return (
                  <tr key={location._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {location.country}
                        </div>
                        <div className="text-xs text-gray-500">
                          {location.countryCode} - {location.currency}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-center">
                      <input
                        type="checkbox"
                        checked={isAvailable}
                        onChange={(e) => {
                          updateLocationPricing(location._id, {
                            isAvailable: e.target.checked,
                          });
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={
                            locationPrice !== undefined ? locationPrice : ""
                          }
                          placeholder={formData.basePrice?.toString() || "0"}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === "") {
                              if (locationPricing) {
                                const { price, ...rest } = locationPricing;
                                updateLocationPricing(location._id, rest);
                              } else {
                                removeLocationPricing(location._id);
                              }
                            } else {
                              const numValue = parseFloat(value);
                              if (!isNaN(numValue) && numValue >= 0) {
                                updateLocationPricing(location._id, {
                                  price: numValue,
                                });
                              }
                            }
                          }}
                          className="w-32"
                          label=""
                        />
                        <span className="text-sm text-gray-600 font-medium">
                          {location.currencySymbol}
                        </span>
                      </div>
                    </td>
                    {isProfitPluginEnabled && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={
                            locationMaterialCost !== undefined
                              ? locationMaterialCost
                              : ""
                          }
                          placeholder={rootMaterialCost?.toString() || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setLocationMaterialCosts((prev) => ({
                              ...prev,
                              [location._id]: value
                                ? parseFloat(value)
                                : undefined,
                            }));
                          }}
                          className="w-32"
                          label=""
                        />
                      </td>
                    )}
                    {!formData.isDigital && (
                      <td className="px-4 py-3 whitespace-nowrap">
                        {(() => {
                          // Check if there are any valid variant options (with name and at least one value)
                          // A variant option is valid only if it has a non-empty name AND at least one non-empty value
                          if (
                            !formData.variantOptions ||
                            !Array.isArray(formData.variantOptions) ||
                            formData.variantOptions.length === 0
                          ) {
                            return true; // No variant options, allow quantity input
                          }

                          // Check if ALL variant options are invalid (empty name or no values)
                          const hasValidVariants = formData.variantOptions.some(
                            (opt: any) => {
                              if (!opt) return false;
                              const hasName =
                                opt.name && opt.name.trim() !== "";
                              if (!hasName) return false; // No name = invalid

                              // Check if there's at least one non-empty value
                              const hasValues =
                                opt.values &&
                                Array.isArray(opt.values) &&
                                opt.values.length > 0 &&
                                opt.values.some(
                                  (val: any) =>
                                    val && val.value && val.value.trim() !== "",
                                );

                              return hasValues; // Valid only if has name AND has values
                            },
                          );

                          return !hasValidVariants; // Allow quantity if no valid variants
                        })() ? (
                          <Input
                            type="number"
                            step="1"
                            min="0"
                            value={
                              locationQuantity !== undefined
                                ? locationQuantity
                                : ""
                            }
                            placeholder={
                              formData.inventoryQuantity?.toString() || ""
                            }
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "") {
                                if (locationPricing) {
                                  const { inventoryQuantity, ...rest } =
                                    locationPricing;
                                  updateLocationPricing(location._id, rest);
                                } else {
                                  removeLocationPricing(location._id);
                                }
                              } else {
                                const numValue = parseInt(value, 10);
                                if (!isNaN(numValue) && numValue >= 0) {
                                  updateLocationPricing(location._id, {
                                    inventoryQuantity: numValue,
                                  });
                                }
                              }
                            }}
                            className="w-32"
                            label=""
                          />
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <Text
                              size="sm"
                              color="muted"
                              className="text-gray-400"
                            >
                              {t("admin.productForm.managedByVariants")}
                            </Text>
                            <span
                              className="text-gray-400 hover:text-gray-600 cursor-help"
                              title={
                                t(
                                  "admin.productForm.removeVariantsToSetQuantity",
                                ) ||
                                "Remove all variant rows to set quantity manually"
                              }
                            >
                              <FaInfoCircle className="text-xs" />
                            </span>
                          </div>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

interface ProductFormContentProps {
  formData: Partial<Product>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Product>>>;
  error: string | null;
  categories: any[];
  t: (key: string) => string;
  currentLanguage: SupportedLanguage;
  handleSubmit: (e: React.FormEvent) => void;
  handleImageUpload: (url: string, type?: "image" | "video") => void;
  handleImageTranslationUpload: (imageIndex: number, url: string) => void;
  handleImageRemove: (index: number) => void;
  handleImageSetPrimary: (index: number) => void;
  addVariantOption: () => void;
  removeVariantOption: (index: number) => void;
  addMetafield: () => void;
  removeMetafield: (index: number) => void;
  productId?: string;
  api: any;
  // Profit settings props
  contributors: any[];
  roles: any[];
  rootMaterialCost?: number;
  setRootMaterialCost: (value: number | undefined) => void;
  locationMaterialCosts: Record<string, number | undefined>;
  setLocationMaterialCosts: React.Dispatch<
    React.SetStateAction<Record<string, number | undefined>>
  >;
  variantCombinationMaterialCosts: Record<string, number | undefined>;
  setVariantCombinationMaterialCosts: React.Dispatch<
    React.SetStateAction<Record<string, number | undefined>>
  >;
  contributorAssignments: Array<{ contributorId: string; roleId: string }>;
  setContributorAssignments: React.Dispatch<
    React.SetStateAction<Array<{ contributorId: string; roleId: string }>>
  >;
  activeTab: "basic" | "pricing" | "variants" | "discounts" | "advanced";
}

// Form content component - defined outside to maintain stable reference
const ProductFormContent: React.FC<ProductFormContentProps> = ({
  formData,
  setFormData,
  error,
  categories,
  t,
  currentLanguage,
  handleSubmit,
  handleImageUpload,
  handleImageTranslationUpload,
  handleImageRemove,
  handleImageSetPrimary,
  addVariantOption,
  removeVariantOption,
  addMetafield,
  removeMetafield,
  productId,
  api,
  contributors,
  roles,
  rootMaterialCost,
  setRootMaterialCost,
  locationMaterialCosts,
  setLocationMaterialCosts,
  variantCombinationMaterialCosts,
  setVariantCombinationMaterialCosts,
  contributorAssignments,
  setContributorAssignments,
  activeTab,
}) => {
  const { isPluginEnabled } = useStore();
  const isProfitPluginEnabled = isPluginEnabled("profit-rules");
  const isCustomizationPluginEnabled = isPluginEnabled("product-customization");
  const isDiscountsPluginEnabled = isPluginEnabled("discounts-promos");
  const [locations, setLocations] = useState<any[]>([]);

  const hasVariantOptions = useMemo(() => {
    if (
      !formData.variantOptions ||
      !Array.isArray(formData.variantOptions) ||
      formData.variantOptions.length === 0
    ) {
      return false;
    }
    return formData.variantOptions.some((opt: any) => {
      if (!opt) return false;
      const hasName = opt.name && opt.name.trim() !== "";
      if (!hasName) return false;
      const hasValues =
        opt.values &&
        Array.isArray(opt.values) &&
        opt.values.length > 0 &&
        opt.values.some(
          (val: any) => val && val.value && val.value.trim() !== "",
        );
      return hasValues;
    });
  }, [formData.variantOptions]);

  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(
    null,
  );
  const [selectedCombination, setSelectedCombination] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!selectedLocationId && locations.length > 0) {
      setSelectedLocationId(String(locations[0]._id));
    }
  }, [locations, selectedLocationId]);

  useEffect(() => {
    if (hasVariantOptions) {
      const combos = (formData.variantCombinations || []).map(
        (c: any) => c.combination,
      );
      if (combos.length > 0) {
        if (!selectedCombination || !combos.includes(selectedCombination)) {
          setSelectedCombination(combos[0]);
        }
      } else {
        if (selectedCombination !== null) {
          setSelectedCombination(null);
        }
      }
    } else if (selectedCombination !== null) {
      setSelectedCombination(null);
    }
  }, [formData.variantCombinations, hasVariantOptions, selectedCombination]);

  // Fetch locations for country mapping
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await api.get("/locations/admin?isActive=true");
        setLocations(response.data.locations || []);
      } catch (err: any) {
        console.error("Failed to fetch locations:", err);
        // Fallback to all countries if locations fetch fails
        setLocations([]);
      }
    };
    fetchLocations();
  }, [api]);

  // Render based on active tab
  const renderTabContent = () => {
    if (activeTab === "basic") {
      return renderBasicTab();
    } else if (activeTab === "variants") {
      return renderVariantsTab();
    } else if (activeTab === "pricing") {
      return renderPricingTab();
    } else if (activeTab === "discounts") {
      return renderDiscountsTab();
    } else if (activeTab === "advanced") {
      return renderAdvancedTab();
    }
    return null;
  };

  const renderDiscountsTab = () => {
    if (!isDiscountsPluginEnabled) {
      return null;
    }

    const availableLocations = locations || [];

    const selectedLocation = selectedLocationId
      ? availableLocations.find(
          (l: any) => String(l._id) === String(selectedLocationId),
        )
      : undefined;

    const getTargetLocationPricing = (
      productOrCombo: any,
      locationId: string,
    ): LocationPricing | undefined => {
      const list: LocationPricing[] = Array.isArray(
        productOrCombo?.locationPricing,
      )
        ? productOrCombo.locationPricing
        : [];
      return list.find(
        (lp: any) => String(lp.locationId) === String(locationId),
      );
    };

    const upsertLocationPricingDiscount = (params: {
      locationId: string;
      discount?: DiscountConfig;
    }) => {
      const { locationId, discount } = params;

      setFormData((prev) => {
        if (hasVariantOptions) {
          const combinationKey = selectedCombination;
          if (!combinationKey) {
            return prev;
          }

          const updatedCombos = (prev.variantCombinations || []).map(
            (combo: any) => {
              if (combo.combination !== combinationKey) {
                return combo;
              }

              const currentPricing: LocationPricing[] = Array.isArray(
                combo.locationPricing,
              )
                ? combo.locationPricing
                : [];

              const idx = currentPricing.findIndex(
                (lp: any) => String(lp.locationId) === String(locationId),
              );

              let nextPricing: LocationPricing[];
              if (idx >= 0) {
                const next = [...currentPricing];
                next[idx] = { ...next[idx], discount };
                nextPricing = next;
              } else {
                nextPricing = [
                  ...currentPricing,
                  { locationId, isAvailable: true, discount },
                ];
              }

              return {
                ...combo,
                locationPricing: nextPricing,
              };
            },
          );

          return {
            ...prev,
            variantCombinations: updatedCombos,
          };
        }

        const currentPricing: LocationPricing[] = Array.isArray(
          prev.locationPricing,
        )
          ? (prev.locationPricing as any)
          : [];

        const idx = currentPricing.findIndex(
          (lp: any) => String(lp.locationId) === String(locationId),
        );

        let nextPricing: LocationPricing[];
        if (idx >= 0) {
          const next = [...currentPricing];
          next[idx] = { ...next[idx], discount };
          nextPricing = next;
        } else {
          nextPricing = [
            ...currentPricing,
            { locationId, isAvailable: true, discount },
          ];
        }

        return {
          ...prev,
          locationPricing: nextPricing,
        };
      });
    };

    const getCurrentDiscount = (): DiscountConfig | undefined => {
      if (!selectedLocationId) {
        return undefined;
      }

      if (hasVariantOptions) {
        const combo = (formData.variantCombinations || []).find(
          (c: any) => c.combination === selectedCombination,
        );
        return getTargetLocationPricing(combo, selectedLocationId)?.discount;
      }

      return getTargetLocationPricing(formData, selectedLocationId)?.discount;
    };

    const currentDiscount = getCurrentDiscount();
    const modeValue: "none" | DiscountMode = currentDiscount?.mode || "none";

    return (
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}

        <div className="space-y-2">
          <h3 className="text-lg font-semibold">
            {t("admin.productForm.discounts") || "Discounts"}
          </h3>
          <Text size="sm" color="muted" className="text-gray-600">
            {t("admin.productForm.discountsHint") ||
              "Discounts are configured per unit and can be static or tiered by quantity."}
          </Text>
        </div>

        {hasVariantOptions && (
          <div className="space-y-2">
            <Select
              label={
                t("admin.productForm.variantCombination") ||
                "Variant combination"
              }
              value={selectedCombination || ""}
              onChange={(e) => setSelectedCombination(e.target.value || null)}
              options={(formData.variantCombinations || []).map((c: any) => ({
                value: c.combination,
                label: c.combination,
              }))}
            />
            {(!formData.variantCombinations ||
              formData.variantCombinations.length === 0) && (
              <Alert variant="info">
                {t("admin.productForm.discountsNeedCombinations") ||
                  "Create variant combinations first before setting variant-country discounts."}
              </Alert>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border border-gray-200 rounded p-2 md:col-span-1">
            <div className="text-sm font-medium text-gray-700 mb-2">
              {t("admin.productForm.country") || "Country"}
            </div>
            <div className="space-y-1 max-h-[360px] overflow-auto">
              {availableLocations.map((loc: any) => {
                const isActive = String(loc._id) === String(selectedLocationId);
                return (
                  <button
                    key={String(loc._id)}
                    type="button"
                    onClick={() => setSelectedLocationId(String(loc._id))}
                    className={`w-full text-left px-2 py-2 rounded text-sm transition-colors ${
                      isActive
                        ? "bg-blue-50 text-blue-700"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span>
                        {loc.country ||
                          getCountryByCode(loc.countryCode)?.name ||
                          loc.countryCode}
                      </span>
                      <span className="text-xs text-gray-500">
                        {loc.currency}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {loc.countryCode}
                    </div>
                  </button>
                );
              })}
              {availableLocations.length === 0 && (
                <div className="text-sm text-gray-600">
                  {t("admin.productForm.noLocations") || "No locations found."}
                </div>
              )}
            </div>
          </div>

          <div className="border border-gray-200 rounded p-3 md:col-span-2">
            {!selectedLocationId ? (
              <div className="text-sm text-gray-600">
                {t("admin.productForm.selectCountry") ||
                  "Select a country to edit discounts."}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-gray-900">
                      {selectedLocation?.country ||
                        getCountryByCode(selectedLocation?.countryCode)?.name ||
                        selectedLocation?.countryCode}
                    </div>
                    <div className="text-xs text-gray-500">
                      {selectedLocation?.countryCode} ·{" "}
                      {selectedLocation?.currency}
                    </div>
                  </div>

                  <div className="min-w-[220px]">
                    <Select
                      label={
                        t("admin.productForm.discountMode") || "Discount mode"
                      }
                      value={modeValue}
                      onChange={(e) => {
                        const next = e.target.value as any;
                        if (next === "none") {
                          upsertLocationPricingDiscount({
                            locationId: selectedLocationId,
                            discount: undefined,
                          });
                          return;
                        }
                        const base: DiscountConfig = {
                          mode: next,
                          type: currentDiscount?.type || "amount",
                          value: currentDiscount?.value ?? 0,
                          tiers: currentDiscount?.tiers,
                        };
                        if (next === "tiered" && !Array.isArray(base.tiers)) {
                          base.tiers = [];
                        }
                        upsertLocationPricingDiscount({
                          locationId: selectedLocationId,
                          discount: base,
                        });
                      }}
                      options={[
                        { value: "none", label: t("admin.none") || "None" },
                        {
                          value: "static",
                          label: t("admin.productForm.static") || "Static",
                        },
                        {
                          value: "tiered",
                          label: t("admin.productForm.tiered") || "Tiered",
                        },
                      ]}
                    />
                  </div>
                </div>

                {currentDiscount && (
                  <div className="space-y-3">
                    <div
                      className={`grid gap-3 ${
                        currentDiscount.mode === "static"
                          ? "grid-cols-2"
                          : "grid-cols-1"
                      }`}
                    >
                      <Select
                        label={t("admin.productForm.discountType") || "Type"}
                        value={currentDiscount.type}
                        onChange={(e) => {
                          upsertLocationPricingDiscount({
                            locationId: selectedLocationId,
                            discount: {
                              ...currentDiscount,
                              type: e.target.value as DiscountType,
                            },
                          });
                        }}
                        options={[
                          {
                            value: "amount",
                            label:
                              t("admin.productForm.discountAmount") || "Amount",
                          },
                          {
                            value: "percentage",
                            label:
                              t("admin.productForm.percentage") || "Percentage",
                          },
                        ]}
                      />

                      {currentDiscount.mode === "static" && (
                        <Input
                          label={
                            currentDiscount.type === "percentage"
                              ? t("admin.productForm.percent") || "Percent"
                              : t("admin.productForm.amountPerUnit") ||
                                "Amount per unit"
                          }
                          type="number"
                          step="0.01"
                          min="0"
                          value={currentDiscount.value}
                          onChange={(e) => {
                            const value = parseFloat(e.target.value) || 0;
                            upsertLocationPricingDiscount({
                              locationId: selectedLocationId,
                              discount: {
                                ...currentDiscount,
                                value,
                              },
                            });
                          }}
                        />
                      )}
                    </div>

                    {currentDiscount.mode === "tiered" && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="text-sm font-medium text-gray-700">
                            {t("admin.productForm.tiers") || "Tiers"}
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const tiers = Array.isArray(currentDiscount.tiers)
                                ? [...currentDiscount.tiers]
                                : [];
                              tiers.push({
                                minQuantity: 1,
                                value: currentDiscount.value,
                              });
                              upsertLocationPricingDiscount({
                                locationId: selectedLocationId,
                                discount: { ...currentDiscount, tiers },
                              });
                            }}
                          >
                            <span className="flex items-center gap-2">
                              <FaPlus />
                              {t("admin.add") || "Add"}
                            </span>
                          </Button>
                        </div>

                        <div className="border border-gray-200 rounded overflow-hidden">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-32">
                                  {t("admin.productForm.minQty") || "Min Qty"}
                                </th>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                                  {t("admin.productForm.value") || "Value"}
                                </th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-700 uppercase tracking-wider w-20"></th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(currentDiscount.tiers || []).map(
                                (tier, idx) => (
                                  <tr key={idx}>
                                    <td className="px-3 py-2">
                                      <Input
                                        label=""
                                        type="number"
                                        step="1"
                                        min="1"
                                        value={tier.minQuantity}
                                        onChange={(e) => {
                                          const tiers = [
                                            ...(currentDiscount.tiers || []),
                                          ];
                                          tiers[idx] = {
                                            ...tiers[idx],
                                            minQuantity: Math.max(
                                              1,
                                              parseInt(e.target.value, 10) || 1,
                                            ),
                                          };
                                          upsertLocationPricingDiscount({
                                            locationId: selectedLocationId,
                                            discount: {
                                              ...currentDiscount,
                                              tiers,
                                            },
                                          });
                                        }}
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      <Input
                                        label=""
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={tier.value}
                                        onChange={(e) => {
                                          const tiers = [
                                            ...(currentDiscount.tiers || []),
                                          ];
                                          tiers[idx] = {
                                            ...tiers[idx],
                                            value:
                                              parseFloat(e.target.value) || 0,
                                          };
                                          upsertLocationPricingDiscount({
                                            locationId: selectedLocationId,
                                            discount: {
                                              ...currentDiscount,
                                              tiers,
                                            },
                                          });
                                        }}
                                      />
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                          const tiers = [
                                            ...(currentDiscount.tiers || []),
                                          ].filter((_, i) => i !== idx);
                                          upsertLocationPricingDiscount({
                                            locationId: selectedLocationId,
                                            discount: {
                                              ...currentDiscount,
                                              tiers,
                                            },
                                          });
                                        }}
                                      >
                                        <FaTrash />
                                      </Button>
                                    </td>
                                  </tr>
                                ),
                              )}
                              {(currentDiscount.tiers || []).length === 0 && (
                                <tr>
                                  <td
                                    className="px-3 py-3 text-sm text-gray-600"
                                    colSpan={3}
                                  >
                                    {t("admin.productForm.noTiers") ||
                                      "No tiers yet."}
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderBasicTab = () => (
    <div className="space-y-3">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Basic Information */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">
          {t("admin.productForm.basicInformation")}
          {currentLanguage !== "en" && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({currentLanguage.toUpperCase()})
            </span>
          )}
        </h3>
        <TranslatableField
          label={t("admin.productForm.productTitle")}
          fieldName="title"
          entity={formData}
          value={formData.title || ""}
          onChange={(language: SupportedLanguage, value: string) => {
            if (language === "en") {
              setFormData((prev) => ({ ...prev, title: value }));
            } else {
              setFormData((prev) => {
                const translations = prev.translations || {};
                const fieldTranslations = translations.title || {};
                fieldTranslations[language] = value;
                return {
                  ...prev,
                  translations: {
                    ...translations,
                    title: fieldTranslations,
                  },
                };
              });
            }
          }}
          required
        />
        <div>
          <TranslatableField
            label={t("admin.description")}
            fieldName="description"
            entity={formData}
            value={formData.description || ""}
            onChange={(language: SupportedLanguage, value: string) => {
              if (language === "en") {
                setFormData((prev) => ({ ...prev, description: value }));
              } else {
                setFormData((prev) => {
                  const translations = prev.translations || {};
                  const fieldTranslations = translations.description || {};
                  fieldTranslations[language] = value;
                  return {
                    ...prev,
                    translations: {
                      ...translations,
                      description: fieldTranslations,
                    },
                  };
                });
              }
            }}
            type="rich"
            height="250px"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={`${t("admin.productForm.vendor")}${currentLanguage !== "en" ? ` (${currentLanguage.toUpperCase()})` : ""}`}
            value={formData.vendor || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, vendor: e.target.value }))
            }
          />
          <Input
            label={`${t("admin.productForm.productType")}${currentLanguage !== "en" ? ` (${currentLanguage.toUpperCase()})` : ""}`}
            value={formData.productType || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, productType: e.target.value }))
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("admin.status")}
            </label>
            <SearchableDropdown
              fetchOptions={async (search: string) => {
                const statuses = [
                  { _id: "draft", label: t("admin.draft") },
                  { _id: "active", label: t("admin.active") },
                  { _id: "archived", label: t("admin.archived") },
                ];
                return statuses.filter((status) =>
                  status.label.toLowerCase().includes(search.toLowerCase()),
                );
              }}
              placeholder={t("admin.productForm.selectStatus")}
              value={formData.status || "draft"}
              getDisplayLabel={(val: string) => {
                const statusMap: Record<string, string> = {
                  draft: t("admin.draft"),
                  active: t("admin.active"),
                  archived: t("admin.archived"),
                };
                return statusMap[val] || val;
              }}
              onSelect={(option) => {
                setFormData((prev) => ({ ...prev, status: option._id as any }));
              }}
            />
          </div>
          <div>
            <MultiSelect
              label={t("admin.categories.title")}
              options={categories.map((category) => {
                const categoryId =
                  typeof category._id === "string"
                    ? category._id
                    : category._id
                      ? String(category._id)
                      : "";
                return {
                  value: categoryId,
                  label: category.name || "",
                };
              })}
              value={formData.categories || []}
              onChange={(selectedCategoryIds) => {
                setFormData((prev) => ({
                  ...prev,
                  categories: selectedCategoryIds,
                }));
              }}
              placeholder={t("admin.productForm.searchCategory")}
              helperText={
                categories.length === 0
                  ? t("admin.categories.noCategories")
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      {/* SEO */}
      <div className="space-y-3 mt-4">
        <h3 className="text-lg font-semibold">
          {t("admin.productForm.seo")}
          {currentLanguage !== "en" && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({currentLanguage.toUpperCase()})
            </span>
          )}
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <Input
            label={`${t("admin.productForm.seoTitle")}${currentLanguage !== "en" ? ` (${currentLanguage.toUpperCase()})` : ""}`}
            value={formData.seoTitle || ""}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, seoTitle: e.target.value }))
            }
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("admin.productForm.seoDescription")}
              {currentLanguage !== "en" && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({currentLanguage.toUpperCase()})
                </span>
              )}
            </label>
            <textarea
              value={formData.seoDescription || ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  seoDescription: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderPricingTab = () => (
    <div className="space-y-3">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Pricing */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">
          {t("admin.productForm.pricing")}
        </h3>
        <div
          className={`grid gap-4 ${isProfitPluginEnabled ? "grid-cols-3" : "grid-cols-2"}`}
        >
          <Input
            label={t("admin.productForm.basePrice")}
            type="number"
            step="0.01"
            required
            value={formData.basePrice || 0}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                basePrice: parseFloat(e.target.value) || 0,
              }))
            }
          />
          {isProfitPluginEnabled && (
            <Input
              label={t("admin.productProfitSettings.materialCosts.rootLevel")}
              type="number"
              step="0.01"
              min="0"
              value={rootMaterialCost || ""}
              onChange={(e) =>
                setRootMaterialCost(
                  e.target.value ? parseFloat(e.target.value) : undefined,
                )
              }
            />
          )}
          <Input
            label={t("admin.productForm.compareAtPrice")}
            type="number"
            step="0.01"
            value={formData.compareAtPrice || ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                compareAtPrice: e.target.value
                  ? parseFloat(e.target.value)
                  : undefined,
              }))
            }
          />
        </div>
      </div>

      {/* Inventory - Hide for digital products */}
      {!formData.isDigital && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {t("admin.productForm.inventory")}
            {currentLanguage !== "en" && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({currentLanguage.toUpperCase()})
              </span>
            )}
          </h3>
          {(() => {
            // Check if there are any valid variant options (with name and at least one value)
            // A variant option is valid only if it has a non-empty name AND at least one non-empty value
            if (
              !formData.variantOptions ||
              !Array.isArray(formData.variantOptions) ||
              formData.variantOptions.length === 0
            ) {
              return true; // No variant options, allow quantity input
            }

            // Check if ALL variant options are invalid (empty name or no values)
            const hasValidVariants = formData.variantOptions.some(
              (opt: any) => {
                if (!opt) return false;
                const hasName = opt.name && opt.name.trim() !== "";
                if (!hasName) return false; // No name = invalid

                // Check if there's at least one non-empty value
                const hasValues =
                  opt.values &&
                  Array.isArray(opt.values) &&
                  opt.values.length > 0 &&
                  opt.values.some(
                    (val: any) => val && val.value && val.value.trim() !== "",
                  );

                return hasValues; // Valid only if has name AND has values
              },
            );

            return !hasValidVariants; // Allow quantity if no valid variants
          })() ? (
            <Input
              label={t("admin.productForm.inventoryQuantity")}
              type="number"
              step="1"
              min="0"
              value={
                formData.inventoryQuantity !== undefined &&
                formData.inventoryQuantity !== null
                  ? formData.inventoryQuantity
                  : ""
              }
              onChange={(e) => {
                const value = e.target.value;
                if (value === "") {
                  // Clear the value (set to undefined)
                  setFormData((prev) => ({
                    ...prev,
                    inventoryQuantity: undefined,
                  }));
                } else {
                  const numValue = parseInt(value, 10);
                  if (!isNaN(numValue) && numValue >= 0) {
                    setFormData((prev) => ({
                      ...prev,
                      inventoryQuantity: numValue,
                    }));
                  }
                }
              }}
            />
          ) : (
            <div className="flex items-center gap-1.5">
              <Text size="sm" color="muted" className="text-gray-600">
                {t("admin.productForm.inventoryManagedByVariants")}
              </Text>
              <span
                className="text-gray-400 hover:text-gray-600 cursor-help"
                title={
                  t("admin.productForm.removeVariantsToSetQuantity") ||
                  "Remove all variant rows to set quantity manually"
                }
              >
                <FaInfoCircle className="text-xs" />
              </span>
            </div>
          )}
        </div>
      )}

      {/* Shipping - Only show for products without valid variants */}
      {!formData.isDigital && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">
            {t("admin.sidebar.shipping") || "Shipping"}
          </h3>
          {(() => {
            // A variant option is considered valid only if it has a non-empty name AND at least one non-empty value
            const hasValidVariants =
              !!formData.variantOptions &&
              Array.isArray(formData.variantOptions) &&
              formData.variantOptions.length > 0 &&
              formData.variantOptions.some((opt: any) => {
                if (!opt) return false;
                const hasName = opt.name && opt.name.trim() !== "";
                if (!hasName) return false;
                const hasValues =
                  opt.values &&
                  Array.isArray(opt.values) &&
                  opt.values.length > 0 &&
                  opt.values.some(
                    (val: any) => val && val.value && val.value.trim() !== "",
                  );
                return hasValues;
              });

            return !hasValidVariants;
          })() ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label={t("admin.productForm.weight") || "Weight"}
                  type="number"
                  step="0.01"
                  min="0"
                  value={
                    formData.weight !== undefined && formData.weight !== null
                      ? formData.weight
                      : ""
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData((prev) => ({
                      ...prev,
                      weight: value ? parseFloat(value) : undefined,
                    }));
                  }}
                />
                <Select
                  label={t("admin.productForm.weightUnit") || "Weight Unit"}
                  value={(formData.weightUnit as any) || "kg"}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      weightUnit: e.target.value as any,
                    }));
                  }}
                  options={[
                    { value: "kg", label: "kg" },
                    { value: "g", label: "g" },
                    { value: "lb", label: "lb" },
                    { value: "oz", label: "oz" },
                  ]}
                />
              </div>

              <div className="grid grid-cols-4 gap-4">
                <Input
                  label={t("admin.productForm.length") || "Length"}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.dimensions?.length ?? ""}
                  onChange={(e) => {
                    const length = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined;
                    setFormData((prev) => ({
                      ...prev,
                      dimensions:
                        length !== undefined
                          ? {
                              ...(prev.dimensions || {
                                width: 0,
                                height: 0,
                                unit: "cm",
                              }),
                              length,
                            }
                          : prev.dimensions,
                    }));
                  }}
                />
                <Input
                  label={t("admin.productForm.width") || "Width"}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.dimensions?.width ?? ""}
                  onChange={(e) => {
                    const width = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined;
                    setFormData((prev) => ({
                      ...prev,
                      dimensions:
                        width !== undefined
                          ? {
                              ...(prev.dimensions || {
                                length: 0,
                                height: 0,
                                unit: "cm",
                              }),
                              width,
                            }
                          : prev.dimensions,
                    }));
                  }}
                />
                <Input
                  label={t("admin.productForm.height") || "Height"}
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.dimensions?.height ?? ""}
                  onChange={(e) => {
                    const height = e.target.value
                      ? parseFloat(e.target.value)
                      : undefined;
                    setFormData((prev) => ({
                      ...prev,
                      dimensions:
                        height !== undefined
                          ? {
                              ...(prev.dimensions || {
                                length: 0,
                                width: 0,
                                unit: "cm",
                              }),
                              height,
                            }
                          : prev.dimensions,
                    }));
                  }}
                />
                <Select
                  label={t("admin.productForm.dimensionUnit") || "Unit"}
                  value={formData.dimensions?.unit || "cm"}
                  onChange={(e) => {
                    const unit = e.target.value as any;
                    setFormData((prev) => ({
                      ...prev,
                      dimensions: prev.dimensions
                        ? { ...prev.dimensions, unit }
                        : { length: 0, width: 0, height: 0, unit },
                    }));
                  }}
                  options={[
                    { value: "cm", label: "cm" },
                    { value: "in", label: "in" },
                  ]}
                />
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <Text size="sm" color="muted" className="text-gray-600">
                {t("admin.productForm.managedByVariants") ||
                  "Managed by variants"}
              </Text>
              <span
                className="text-gray-400 hover:text-gray-600 cursor-help"
                title={
                  t("admin.productForm.removeVariantsToSetQuantity") ||
                  "Remove all variant rows to edit product-level shipping"
                }
              >
                <FaInfoCircle className="text-xs" />
              </span>
            </div>
          )}
        </div>
      )}

      {/* Country Management - Only show in English */}
      {currentLanguage === "en" && (
        <LocationManagementSection
          formData={formData}
          setFormData={setFormData}
          locationMaterialCosts={locationMaterialCosts}
          setLocationMaterialCosts={setLocationMaterialCosts}
          rootMaterialCost={rootMaterialCost}
        />
      )}
    </div>
  );

  const renderVariantsTab = () => (
    <div className="space-y-3">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Variant Options */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {t("admin.productForm.variantOptions")}
            {currentLanguage !== "en" && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({currentLanguage.toUpperCase()})
              </span>
            )}
          </h3>
          {currentLanguage === "en" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addVariantOption();
              }}
            >
              {t("admin.productForm.addOption")}
            </Button>
          )}
        </div>

        {/* English: Show full management UI */}
        {currentLanguage === "en" &&
          (formData.variantOptions || []).length > 0 && (
            <VariantOptionsManager
              variantOptions={formData.variantOptions || []}
              setFormData={setFormData}
              formData={formData}
              t={t}
              removeVariantOption={removeVariantOption}
            />
          )}

        {/* Other languages: Show translation UI */}
        {currentLanguage !== "en" && (
          <>
            {(formData.variantOptions || []).length === 0 ? (
              <div className="border border-gray-300 p-4 text-center text-gray-600 bg-gray-50 rounded">
                <p className="text-sm">
                  Switch to English to add variant options. Then return here to
                  translate them.
                </p>
              </div>
            ) : (
              <div className="border border-gray-200 rounded overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider w-40 sticky left-0 bg-gray-50 z-10">
                        {t("admin.productForm.optionName")} (
                        {currentLanguage.toUpperCase()})
                      </th>
                      {(() => {
                        // Find the maximum number of values across all options
                        const maxValues = Math.max(
                          ...(formData.variantOptions || []).map(
                            (opt) => opt.values.length,
                          ),
                          0,
                        );
                        // Get all unique value positions to create columns
                        return Array.from(
                          { length: maxValues },
                          (_, i) => i,
                        ).map((valueIndex) => {
                          // Find the value at this index from the first option that has it
                          const sampleValue = (
                            formData.variantOptions || []
                          ).find((opt) => opt.values[valueIndex])?.values[
                            valueIndex
                          ];
                          return (
                            <th
                              key={valueIndex}
                              className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider min-w-[200px]"
                            >
                              {sampleValue ? (
                                <div className="space-y-1">
                                  <div className="text-xs text-gray-500 font-normal">
                                    {t("admin.productForm.value")}{" "}
                                    {valueIndex + 1}
                                  </div>
                                  <div className="text-xs text-gray-400 font-normal">
                                    Original: {sampleValue.value}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs text-gray-500 font-normal">
                                  {t("admin.productForm.value")}{" "}
                                  {valueIndex + 1}
                                </div>
                              )}
                            </th>
                          );
                        });
                      })()}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {(formData.variantOptions || []).map(
                      (option, optionIndex) => {
                        const optionNameTranslation =
                          option.translations?.[currentLanguage] || "";
                        const hasOptionNameTranslation =
                          !!optionNameTranslation;
                        const maxValues = Math.max(
                          ...(formData.variantOptions || []).map(
                            (opt) => opt.values.length,
                          ),
                          0,
                        );

                        return (
                          <tr key={optionIndex} className="hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap sticky left-0 bg-white z-10">
                              <div className="space-y-1">
                                <div className="text-xs text-gray-500">
                                  Original:{" "}
                                  <span className="font-medium">
                                    {option.name}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={optionNameTranslation}
                                    onChange={(e) => {
                                      setFormData((prev) => {
                                        const newOptions = [
                                          ...(prev.variantOptions || []),
                                        ];
                                        const translations = {
                                          ...(newOptions[optionIndex]
                                            .translations || {}),
                                        };
                                        translations[currentLanguage] =
                                          e.target.value;
                                        newOptions[optionIndex] = {
                                          ...newOptions[optionIndex],
                                          translations,
                                        };
                                        return {
                                          ...prev,
                                          variantOptions: newOptions,
                                        };
                                      });
                                    }}
                                    placeholder={`Translate "${option.name}"`}
                                    className="flex-1"
                                    label=""
                                  />
                                  {hasOptionNameTranslation && (
                                    <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded whitespace-nowrap">
                                      ✓
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            {Array.from(
                              { length: maxValues },
                              (_, valueIndex) => {
                                const value = option.values[valueIndex];
                                const valueTranslation =
                                  value?.translations?.[currentLanguage] || "";
                                const hasValueTranslation = !!valueTranslation;

                                return (
                                  <td key={valueIndex} className="px-3 py-2">
                                    {value ? (
                                      <div className="space-y-1">
                                        <div className="text-xs text-gray-500">
                                          Original:{" "}
                                          <span className="font-medium">
                                            {value.value}
                                          </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <Input
                                            value={valueTranslation}
                                            onChange={(e) => {
                                              setFormData((prev) => {
                                                const newOptions = [
                                                  ...(prev.variantOptions ||
                                                    []),
                                                ];
                                                const newValues = [
                                                  ...newOptions[optionIndex]
                                                    .values,
                                                ];
                                                const translations = {
                                                  ...(newValues[valueIndex]
                                                    .translations || {}),
                                                };
                                                translations[currentLanguage] =
                                                  e.target.value;
                                                newValues[valueIndex] = {
                                                  ...newValues[valueIndex],
                                                  translations,
                                                };
                                                newOptions[optionIndex] = {
                                                  ...newOptions[optionIndex],
                                                  values: newValues,
                                                };
                                                return {
                                                  ...prev,
                                                  variantOptions: newOptions,
                                                };
                                              });
                                            }}
                                            placeholder={`Translate "${value.value}"`}
                                            className="flex-1"
                                            label=""
                                          />
                                          {hasValueTranslation && (
                                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded whitespace-nowrap">
                                              ✓
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="text-xs text-gray-400 italic">
                                        —
                                      </div>
                                    )}
                                  </td>
                                );
                              },
                            )}
                          </tr>
                        );
                      },
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Images */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">
          {t("admin.images")}
          {currentLanguage !== "en" && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({currentLanguage.toUpperCase()})
            </span>
          )}
        </h3>
        <div className="space-y-4">
          <FileUploader
            onUploadComplete={handleImageUpload}
            onImageTranslationUpload={handleImageTranslationUpload}
            existingImages={formData.images || []}
            onImageRemove={handleImageRemove}
            onImageSetPrimary={handleImageSetPrimary}
            currentLanguage={currentLanguage}
            additionalActions={
              <GalleryImageSelector
                onSelect={(url) => handleImageUpload(url, "image")}
                label=""
                buttonText={t("admin.gallery.selectFromGallery")}
              />
            }
          />
        </div>

        {/* Variant Combinations Table */}
        {formData.variantOptions && formData.variantOptions.length > 0 && (
          <VariantCombinationsTable
            variantOptions={(formData.variantOptions || []).map((opt) => ({
              name: opt.name,
              values: opt.values,
              required: opt.required,
            }))}
            images={formData.images || []}
            basePrice={formData.basePrice || 0}
            baseMaterialCost={rootMaterialCost}
            variantCombinations={formData.variantCombinations || []}
            onChange={(combinations) => {
              setFormData((prev) => ({
                ...prev,
                variantCombinations: combinations,
              }));
            }}
            variantCombinationMaterialCosts={variantCombinationMaterialCosts}
            setVariantCombinationMaterialCosts={
              setVariantCombinationMaterialCosts
            }
            isDigital={formData.isDigital}
          />
        )}
      </div>
    </div>
  );

  const renderAdvancedTab = () => (
    <div className="space-y-3">
      {error && <Alert variant="error">{error}</Alert>}

      {/* Tags */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t("admin.productForm.tags")}
          {currentLanguage !== "en" && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              ({currentLanguage.toUpperCase()})
            </span>
          )}
        </label>
        <MultiValueInput
          values={formData.tags || []}
          onChange={(newTags) => {
            setFormData((prev) => ({ ...prev, tags: newTags }));
          }}
          placeholder={
            t("admin.productForm.enterTags") ||
            "Enter tags (press Enter or comma to add)"
          }
          separator=","
        />
      </div>

      {/* Metafields */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">
            {t("admin.productForm.metafields")}
            {currentLanguage !== "en" && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({currentLanguage.toUpperCase()})
              </span>
            )}
          </h3>
          {currentLanguage === "en" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addMetafield();
              }}
            >
              {t("admin.productForm.addMetafield")}
            </Button>
          )}
        </div>
        {(formData.metafields || []).map((metafield, index) => (
          <div key={index} className="flex gap-2">
            <Input
              label={t("admin.productForm.key")}
              value={metafield.key}
              onChange={(e) => {
                setFormData((prev) => {
                  const newMetafields = [...(prev.metafields || [])];
                  newMetafields[index].key = e.target.value;
                  return { ...prev, metafields: newMetafields };
                });
              }}
              className="flex-1"
            />
            <Input
              label={`${t("admin.productForm.value")}${currentLanguage !== "en" ? ` (${currentLanguage.toUpperCase()})` : ""}`}
              value={metafield.value}
              onChange={(e) => {
                setFormData((prev) => {
                  const newMetafields = [...(prev.metafields || [])];
                  newMetafields[index].value = e.target.value;
                  return { ...prev, metafields: newMetafields };
                });
              }}
              className="flex-1"
            />
            <Input
              label={`${t("admin.productForm.namespace")}${currentLanguage !== "en" ? ` (${currentLanguage.toUpperCase()})` : ""}`}
              value={metafield.namespace || ""}
              onChange={(e) => {
                setFormData((prev) => {
                  const newMetafields = [...(prev.metafields || [])];
                  newMetafields[index].namespace = e.target.value;
                  return { ...prev, metafields: newMetafields };
                });
              }}
              className="w-32"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                removeMetafield(index);
              }}
              className="mt-6"
            >
              ×
            </Button>
          </div>
        ))}
      </div>

      {/* Customization Options */}
      {isCustomizationPluginEnabled && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold">
            {t("admin.productForm.customization") || "Customization Options"}
          </h3>
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isCustomizable"
              checked={formData.isCustomizable || false}
              onChange={(e) => {
                setFormData((prev) => ({
                  ...prev,
                  isCustomizable: e.target.checked,
                  // Clear customizationType if disabling customization
                  customizationType: e.target.checked
                    ? prev.customizationType
                    : undefined,
                }));
              }}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label
              htmlFor="isCustomizable"
              className="text-sm font-medium text-gray-700"
            >
              {t("admin.productForm.isCustomizable") ||
                "Product is customizable"}
            </label>
          </div>
          {formData.isCustomizable && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t("admin.productForm.customizationType") ||
                    "Customization Type"}
                </label>
                <SearchableDropdown
                  fetchOptions={async (search: string) => {
                    const types = [
                      { _id: "2d-print", label: "2D Print (Sublimation)" },
                      { _id: "3d-print", label: "3D Print" },
                    ];
                    return types.filter((type) =>
                      type.label.toLowerCase().includes(search.toLowerCase()),
                    );
                  }}
                  placeholder={t("common.placeholders.selectCustomizationType")}
                  value={formData.customizationType || ""}
                  getDisplayLabel={(val: string) => {
                    const typeMap: Record<string, string> = {
                      "2d-print": "2D Print (Sublimation)",
                      "3d-print": "3D Print",
                    };
                    return typeMap[val] || val;
                  }}
                  onSelect={(option) => {
                    setFormData((prev) => ({
                      ...prev,
                      customizationType: option._id as "2d-print" | "3d-print",
                    }));
                  }}
                />
              </div>
              {formData.customizationType === "2d-print" && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Canvas Size
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Select the canvas dimensions for this customizable
                      product. This determines the design area available to
                      customers.
                    </p>
                    <Select
                      options={[
                        {
                          value: "mug",
                          label: t("admin.productForm.canvasSizeMug") || "Mug",
                        },
                        {
                          value: "bottle",
                          label:
                            t("admin.productForm.canvasSizeBottle") || "Bottle",
                        },
                      ]}
                      value={formData.canvasSize || "mug"}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          canvasSize: e.target.value,
                        }));
                      }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Customization Assets
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Select backgrounds and shapes from your asset library that
                      customers can use when customizing this product.
                    </p>
                    <AssetSelector
                      selectedAssetIds={formData.availableAssetIds || []}
                      onSelectionChange={(assetIds) => {
                        setFormData((prev) => ({
                          ...prev,
                          availableAssetIds: assetIds,
                        }));
                      }}
                      selectedFonts={formData.allowedDesignerFonts || []}
                      onFontsChange={(fonts) => {
                        setFormData((prev) => ({
                          ...prev,
                          allowedDesignerFonts: fonts,
                        }));
                      }}
                      types={["background", "shape"]}
                      showFonts={true}
                    />
                  </div>
                </>
              )}
              {formData.customizationType === "3d-print" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Colors
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Specify the colors customers can choose from when
                    customizing this product. Leave empty to allow full color
                    picker.
                  </p>
                  <div className="space-y-2">
                    {(formData.availableColors || []).map((color, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <input
                          type="color"
                          value={color}
                          onChange={(e) => {
                            const newColors = [
                              ...(formData.availableColors || []),
                            ];
                            newColors[index] = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              availableColors: newColors,
                            }));
                          }}
                          className="w-12 h-10 p-0 border border-gray-300 rounded cursor-pointer"
                        />
                        <Input
                          value={color}
                          onChange={(e) => {
                            const newColors = [
                              ...(formData.availableColors || []),
                            ];
                            newColors[index] = e.target.value;
                            setFormData((prev) => ({
                              ...prev,
                              availableColors: newColors,
                            }));
                          }}
                          placeholder="#000000"
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const newColors = (
                              formData.availableColors || []
                            ).filter((_, i) => i !== index);
                            setFormData((prev) => ({
                              ...prev,
                              availableColors: newColors,
                            }));
                          }}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          availableColors: [
                            ...(prev.availableColors || []),
                            "#000000",
                          ],
                        }));
                      }}
                    >
                      Add Color
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Product Options */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">
          {t("admin.productForm.productOptions") || "Product Options"}
        </h3>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="featured"
            checked={formData.featured || false}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                featured: e.target.checked,
              }));
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label
            htmlFor="featured"
            className="text-sm font-medium text-gray-700"
          >
            {t("admin.productForm.featured") || "Feature this product"}
          </label>
        </div>
      </div>

      {/* Digital Product */}
      <div className="space-y-3">
        <h3 className="text-lg font-semibold">
          {t("admin.productForm.digitalProduct") || "Digital Product"}
        </h3>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="isDigital"
            checked={formData.isDigital || false}
            onChange={(e) => {
              setFormData((prev) => ({
                ...prev,
                isDigital: e.target.checked,
              }));
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label
            htmlFor="isDigital"
            className="text-sm font-medium text-gray-700"
          >
            {t("admin.productForm.isDigital") || "This is a digital product"}
          </label>
        </div>
        {formData.isDigital && (
          <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t("admin.productForm.digitalFiles") || "Digital Files"}
              </label>
              <p className="text-sm text-gray-500 mb-3">
                {t("admin.productForm.digitalFilesDescription") ||
                  "Upload files that will be sent to customers when the order is delivered. You can map each file to specific countries and variant combinations."}
              </p>
              <input
                type="file"
                id="digitalFileUpload"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file || !productId) return;

                  try {
                    const formDataUpload = new FormData();
                    formDataUpload.append("file", file);
                    const variantMapping = (
                      document.getElementById(
                        "digitalFileVariantMapping",
                      ) as HTMLSelectElement
                    )?.value;
                    if (variantMapping && variantMapping !== "product") {
                      formDataUpload.append(
                        "variantCombination",
                        variantMapping,
                      );
                    }

                    const response = await api.post(
                      `/products/${productId}/digital-files`,
                      formDataUpload,
                      {
                        headers: {
                          "Content-Type": "multipart/form-data",
                        },
                      },
                    );

                    if (response.data.file) {
                      // If file is mapped to variant, update variantCombinations
                      if (response.data.variantCombination) {
                        setFormData((prev) => {
                          const updatedCombinations = (
                            prev.variantCombinations || []
                          ).map((combo: any) => {
                            if (
                              combo.combination ===
                              response.data.variantCombination
                            ) {
                              return {
                                ...combo,
                                digitalFiles: [
                                  ...(combo.digitalFiles || []),
                                  response.data.file,
                                ],
                              };
                            }
                            return combo;
                          });
                          return {
                            ...prev,
                            variantCombinations: updatedCombinations,
                          };
                        });
                      } else {
                        // Product-level file
                        setFormData((prev) => ({
                          ...prev,
                          digitalFiles: [
                            ...(prev.digitalFiles || []),
                            response.data.file,
                          ],
                        }));
                      }
                    }
                  } catch (err: any) {
                    console.error("Failed to upload digital file:", err);
                    alert(
                      err.response?.data?.error ||
                        t("admin.productForm.uploadFileError") ||
                        "Failed to upload file",
                    );
                  } finally {
                    // Reset input
                    e.target.value = "";
                  }
                }}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                disabled={!productId}
              />
              {!productId && (
                <p className="text-xs text-gray-500 mt-1">
                  {t("admin.productForm.saveProductFirst") ||
                    "Please save the product first before uploading files."}
                </p>
              )}
            </div>
            {/* Product-Level Files */}
            {formData.digitalFiles && formData.digitalFiles.length > 0 && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  {t("admin.productForm.productLevelFiles") ||
                    "Product Level Files"}
                </label>
                <ul className="space-y-3">
                  {formData.digitalFiles.map((file, index) => {
                    const fileCountryCodes = file.countryCodes || [];
                    const availableCountries =
                      locations.length > 0
                        ? locations.map((loc: any) => {
                            const country = getCountryByCode(loc.countryCode);
                            return {
                              code: loc.countryCode,
                              name:
                                country?.name || loc.country || loc.countryCode,
                            };
                          })
                        : COUNTRIES.map((c) => ({
                            code: c.code,
                            name: c.name,
                          }));

                    return (
                      <li
                        key={file.key || index}
                        className="p-3 bg-white border border-gray-200 rounded-lg space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <span className="text-sm font-medium text-gray-900">
                              {file.filename}
                            </span>
                            {file.size && (
                              <span className="text-xs text-gray-500 ml-2">
                                ({(file.size / 1024 / 1024).toFixed(2)} MB)
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={async () => {
                              if (!productId) {
                                // Just remove from local state
                                setFormData((prev) => ({
                                  ...prev,
                                  digitalFiles: (
                                    prev.digitalFiles || []
                                  ).filter((f) => f.key !== file.key),
                                }));
                                return;
                              }

                              // TODO: Add delete endpoint for digital files
                              // For now, just remove from local state
                              setFormData((prev) => ({
                                ...prev,
                                digitalFiles: (prev.digitalFiles || []).filter(
                                  (f) => f.key !== file.key,
                                ),
                              }));
                            }}
                            className="ml-2 text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            {t("common.delete") || "Delete"}
                          </button>
                        </div>
                        <div className="mt-2">
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            {t("admin.productForm.fileCountries") ||
                              "Available Countries"}
                            <span className="text-gray-500 font-normal ml-1">
                              (
                              {t("admin.productForm.fileCountriesHelper") ||
                                "Leave empty for all countries"}
                              )
                            </span>
                          </label>
                          {(() => {
                            // Check if there are any valid variant options (with name and at least one value)
                            const hasValidVariants =
                              formData.variantOptions &&
                              Array.isArray(formData.variantOptions) &&
                              formData.variantOptions.length > 0 &&
                              formData.variantOptions.some((opt: any) => {
                                if (!opt) return false;
                                const hasName =
                                  opt.name && opt.name.trim() !== "";
                                if (!hasName) return false;
                                const hasValues =
                                  opt.values &&
                                  Array.isArray(opt.values) &&
                                  opt.values.length > 0 &&
                                  opt.values.some(
                                    (val: any) =>
                                      val &&
                                      val.value &&
                                      val.value.trim() !== "",
                                  );
                                return hasValues;
                              });

                            // Check if single country store (no mapping needed)
                            const isSingleCountry = locations.length === 1;

                            if (hasValidVariants) {
                              // Show "Managed by variants" message
                              return (
                                <div className="flex items-center gap-1.5">
                                  <Text
                                    size="sm"
                                    color="muted"
                                    className="text-gray-400"
                                  >
                                    {t("admin.productForm.managedByVariants") ||
                                      "Managed by variants"}
                                  </Text>
                                  <span
                                    className="text-gray-400 hover:text-gray-600 cursor-help"
                                    title={
                                      t(
                                        "admin.productForm.fileCountriesManagedByVariants",
                                      ) ||
                                      "Country mapping is managed per variant combination"
                                    }
                                  >
                                    <FaInfoCircle className="text-xs" />
                                  </span>
                                </div>
                              );
                            } else if (isSingleCountry) {
                              // Single country store - no mapping needed
                              return (
                                <Text
                                  size="sm"
                                  color="muted"
                                  className="text-gray-400"
                                >
                                  {t(
                                    "admin.productForm.singleCountryNoMapping",
                                  ) ||
                                    "Single country store - no mapping needed"}
                                </Text>
                              );
                            } else {
                              // Use MultiSelect for country selection
                              return (
                                <MultiSelect
                                  options={availableCountries.map(
                                    (c: { code: string; name: string }) => ({
                                      value: c.code,
                                      label: c.name,
                                    }),
                                  )}
                                  value={fileCountryCodes}
                                  onChange={(newCountryCodes) => {
                                    setFormData((prev) => ({
                                      ...prev,
                                      digitalFiles: (
                                        prev.digitalFiles || []
                                      ).map((f) =>
                                        f.key === file.key
                                          ? {
                                              ...f,
                                              countryCodes:
                                                newCountryCodes.length > 0
                                                  ? newCountryCodes
                                                  : undefined,
                                            }
                                          : f,
                                      ),
                                    }));
                                  }}
                                  placeholder={
                                    t("admin.productForm.selectCountries") ||
                                    "Select countries..."
                                  }
                                  className="text-sm"
                                />
                              );
                            }
                          })()}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Variant-Level Files */}
            {formData.variantCombinations &&
              formData.variantCombinations.length > 0 &&
              formData.digitalFiles &&
              formData.digitalFiles.length > 0 && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    {t("admin.productForm.variantLevelFiles") ||
                      "Variant Level Files"}
                  </label>
                  <p className="text-xs text-gray-500 mb-3">
                    {t("admin.productForm.variantLevelFilesDescription") ||
                      "Select which digital files belong to each variant combination and map them to specific countries."}
                  </p>
                  {formData.variantCombinations.map((combo: any) => {
                    const variantFiles = combo.digitalFiles || [];
                    const selectedFileKeys = variantFiles.map(
                      (f: any) => f.key,
                    );
                    const availableCountries =
                      locations.length > 0
                        ? locations.map((loc: any) => {
                            const country = getCountryByCode(loc.countryCode);
                            return {
                              code: loc.countryCode,
                              name:
                                country?.name || loc.country || loc.countryCode,
                            };
                          })
                        : COUNTRIES.map((c) => ({
                            code: c.code,
                            name: c.name,
                          }));

                    return (
                      <div
                        key={combo.combination}
                        className="space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50"
                      >
                        <div className="text-sm font-semibold text-gray-700">
                          {combo.combination.replace(
                            /\|/g,
                            ` ${t("common.variantSeparator") || "+"} `,
                          )}
                        </div>

                        {/* Multi-select for selecting files for this variant */}
                        <div className="space-y-2">
                          <label className="block text-xs font-medium text-gray-700">
                            {t("admin.productForm.selectFilesForVariant") ||
                              "Select Files for this Variant"}
                          </label>
                          <MultiSelect
                            options={(formData.digitalFiles || []).map(
                              (file: any) => ({
                                value: file.key,
                                label: file.filename,
                              }),
                            )}
                            value={selectedFileKeys}
                            onChange={(selectedKeys) => {
                              // Get the selected files from product-level digitalFiles
                              const selectedFiles = (
                                formData.digitalFiles || []
                              )
                                .filter((file: any) =>
                                  selectedKeys.includes(file.key),
                                )
                                .map((file: any) => {
                                  // Preserve existing country codes if file was already in variant
                                  const existingFile = variantFiles.find(
                                    (f: any) => f.key === file.key,
                                  );
                                  return {
                                    ...file,
                                    countryCodes:
                                      existingFile?.countryCodes || undefined,
                                  };
                                });

                              setFormData((prev) => ({
                                ...prev,
                                variantCombinations: (
                                  prev.variantCombinations || []
                                ).map((vc: any) =>
                                  vc.combination === combo.combination
                                    ? { ...vc, digitalFiles: selectedFiles }
                                    : vc,
                                ),
                              }));
                            }}
                            placeholder={
                              t("admin.productForm.selectFilesForVariant") ||
                              "Select files..."
                            }
                            className="text-sm"
                          />
                        </div>

                        {/* List selected files with country mapping */}
                        {variantFiles.length > 0 && (
                          <div className="space-y-2 mt-3">
                            <label className="block text-xs font-medium text-gray-700">
                              {t("admin.productForm.mapFilesToCountries") ||
                                "Map Files to Countries"}
                            </label>
                            <ul className="space-y-2">
                              {variantFiles.map(
                                (file: any, fileIndex: number) => {
                                  const fileCountryCodes =
                                    file.countryCodes || [];

                                  return (
                                    <li
                                      key={file.key || fileIndex}
                                      className="p-2 bg-white border border-gray-200 rounded space-y-2"
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                          <span className="text-sm font-medium text-gray-900">
                                            {file.filename}
                                          </span>
                                          {file.size && (
                                            <span className="text-xs text-gray-500 ml-2">
                                              (
                                              {(
                                                file.size /
                                                1024 /
                                                1024
                                              ).toFixed(2)}{" "}
                                              {t("common.mb") || "MB"})
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="mt-2">
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                          {t(
                                            "admin.productForm.fileCountries",
                                          ) || "Available Countries"}
                                          <span className="text-gray-500 font-normal ml-1">
                                            (
                                            {t(
                                              "admin.productForm.fileCountriesHelper",
                                            ) ||
                                              "Leave empty for all countries"}
                                            )
                                          </span>
                                        </label>
                                        <MultiSelect
                                          options={availableCountries.map(
                                            (c: {
                                              code: string;
                                              name: string;
                                            }) => ({
                                              value: c.code,
                                              label: c.name,
                                            }),
                                          )}
                                          value={fileCountryCodes}
                                          onChange={(newCountryCodes) => {
                                            setFormData((prev) => ({
                                              ...prev,
                                              variantCombinations: (
                                                prev.variantCombinations || []
                                              ).map((vc: any) =>
                                                vc.combination ===
                                                combo.combination
                                                  ? {
                                                      ...vc,
                                                      digitalFiles: (
                                                        vc.digitalFiles || []
                                                      ).map((f: any) =>
                                                        f.key === file.key
                                                          ? {
                                                              ...f,
                                                              countryCodes:
                                                                newCountryCodes.length >
                                                                0
                                                                  ? newCountryCodes
                                                                  : undefined,
                                                            }
                                                          : f,
                                                      ),
                                                    }
                                                  : vc,
                                              ),
                                            }));
                                          }}
                                          placeholder={
                                            t(
                                              "admin.productForm.selectCountries",
                                            ) || "Select countries..."
                                          }
                                          className="text-sm"
                                        />
                                      </div>
                                    </li>
                                  );
                                },
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
          </div>
        )}
      </div>

      {/* Profit Settings Section - Contributor Assignments */}
      {isProfitPluginEnabled && (
        <div className="space-y-3 border-t pt-4 mt-4">
          <h3 className="text-lg font-semibold">
            {t("admin.productProfitSettings.title")}
          </h3>

          {/* Contributor Assignments */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              {t("admin.productProfitSettings.contributorAssignments.title")}
            </label>
            <Text className="text-sm text-gray-600 dark:text-gray-400">
              {t(
                "admin.productProfitSettings.contributorAssignments.description",
              )}
            </Text>

            <div className="space-y-3">
              {contributorAssignments.map((assignment, index) => {
                const contributorOptions = [
                  {
                    value: "",
                    label: t("admin.productProfitSettings.selectContributor"),
                  },
                  ...contributors.map((c) => ({
                    value: String(c._id || c.id || ""),
                    label: c.name || "",
                  })),
                ];
                const roleOptions = [
                  {
                    value: "",
                    label: t("admin.productProfitSettings.selectRole"),
                  },
                  ...roles.map((r) => ({
                    value: String(r._id || r.id || ""),
                    label: r.name || "",
                  })),
                ];

                return (
                  <div key={index} className="flex gap-2 items-end">
                    <Select
                      key={`contributor-${index}-${contributors.length}-${assignment.contributorId}`}
                      label={t("admin.productProfitSettings.selectContributor")}
                      value={
                        assignment.contributorId
                          ? String(assignment.contributorId)
                          : ""
                      }
                      onChange={(e) => {
                        const updated = [...contributorAssignments];
                        updated[index] = {
                          ...updated[index],
                          contributorId: e.target.value,
                        };
                        setContributorAssignments(updated);
                      }}
                      className="flex-1"
                      options={contributorOptions}
                      placeholder={t(
                        "admin.productProfitSettings.selectContributor",
                      )}
                    />
                    <Select
                      key={`role-${index}-${roles.length}-${assignment.roleId}`}
                      label={t("admin.productProfitSettings.selectRole")}
                      value={assignment.roleId ? String(assignment.roleId) : ""}
                      onChange={(e) => {
                        const updated = [...contributorAssignments];
                        updated[index] = {
                          ...updated[index],
                          roleId: e.target.value,
                        };
                        setContributorAssignments(updated);
                      }}
                      className="flex-1"
                      options={roleOptions}
                      placeholder={t("admin.productProfitSettings.selectRole")}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        setContributorAssignments(
                          contributorAssignments.filter((_, i) => i !== index),
                        )
                      }
                    >
                      <FaTrash />
                    </Button>
                  </div>
                );
              })}
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={() =>
                setContributorAssignments([
                  ...contributorAssignments,
                  { contributorId: "", roleId: "" },
                ])
              }
            >
              <FaPlus className="mr-2" />
              {t("admin.productProfitSettings.addAssignment")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <form id="product-form" onSubmit={handleSubmit} className="space-y-3">
      {renderTabContent()}
    </form>
  );
};

// Memoize ProductFormContent to prevent unnecessary re-renders
const MemoizedProductFormContent = memo(ProductFormContent);

export const ProductFormModal: React.FC<ProductFormModalProps> = ({
  isOpen,
  onClose,
  productId,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { api } = useApi();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);

  // Profit settings state
  const [contributors, setContributors] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [rootMaterialCost, setRootMaterialCost] = useState<number | undefined>(
    undefined,
  );
  const [locationMaterialCosts, setLocationMaterialCosts] = useState<
    Record<string, number | undefined>
  >({});
  const [variantOptionValueMaterialCosts, setVariantOptionValueMaterialCosts] =
    useState<Record<string, number | undefined>>({});
  const [variantCombinationMaterialCosts, setVariantCombinationMaterialCosts] =
    useState<Record<string, number | undefined>>({});
  const [contributorAssignments, setContributorAssignments] = useState<
    Array<{ contributorId: string; roleId: string }>
  >([]);
  const [formData, setFormData] = useState<Partial<Product>>({
    title: "",
    description: "",
    status: "draft",
    basePrice: 0,
    variantOptions: [],
    images: [],
    tags: [],
    categories: [],
    giftCard: false,
    featured: false,
    isCustomizable: false,
    isDigital: false,
    digitalFiles: [],
    metafields: [],
    canvasSize: undefined,
    backgroundImages: [],
    availableAssetIds: [],
    allowedDesignerFonts: [],
    availableColors: [],
    translations: {},
    // Don't default inventoryQuantity - let it be undefined if not set
  });

  // Refs to track previous values and prevent infinite loops
  const prevIsOpenRef = useRef<boolean>(false);
  const prevProductIdRef = useRef<string | undefined>(undefined);
  const fetchCategoriesRef = useRef<(() => Promise<void>) | null>(null);
  const fetchProductRef = useRef<(() => Promise<void>) | null>(null);

  const fetchCategories = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get("/categories?includeInactive=true");
      // Backend returns { categories: [...], flat: [...] }
      // Use the 'flat' property which is already a flat array
      setCategories(response?.data?.flat || []);
    } catch (err: any) {
      console.error("Failed to fetch categories:", err);
      setCategories([]);
    }
  }, [api]);

  const fetchProduct = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      // Backend will automatically return USD for admin users (no conversion)
      const [productResponse, contributorsResponse, rolesResponse] =
        await Promise.all([
          api.get(`/products/admin/${productId}`),
          api.get("/profit-rules/contributors").catch(() => ({ data: [] })),
          api.get("/profit-rules/roles").catch(() => ({ data: [] })),
        ]);
      const productData = productResponse.data;
      setContributors(contributorsResponse.data || []);
      setRoles(rolesResponse.data || []);

      // Extract category IDs from various formats
      // Backend returns 'categories' as array of populated objects or ObjectIds
      let categoryIds: string[] = [];

      if (productData.categories && Array.isArray(productData.categories)) {
        const mappedCategories = productData.categories.map((cat: any): string | null => {
          if (typeof cat === "string") {
            return cat;
          } else if (cat && typeof cat === "object") {
            // Handle populated category object or ObjectId
            if (cat._id) {
              return typeof cat._id === "string" ? cat._id : cat._id.toString();
            } else if (cat.toString && typeof cat.toString === "function") {
              // Handle direct ObjectId
              return cat.toString();
            } else {
              return String(cat);
            }
          }
          return null;
        });
        categoryIds = mappedCategories.filter((id: string | null): id is string => id !== null);
      } else if (productData.category) {
        // Legacy support: if backend still returns single category, convert to array
        let categoryId: string | undefined = undefined;
        if (typeof productData.category === "string") {
          categoryId = productData.category;
        } else if (
          productData.category &&
          typeof productData.category === "object"
        ) {
          if (productData.category._id) {
            categoryId =
              typeof productData.category._id === "string"
                ? productData.category._id
                : productData.category._id.toString();
          } else if (
            productData.category.toString &&
            typeof productData.category.toString === "function"
          ) {
            categoryId = productData.category.toString();
          } else {
            categoryId = String(productData.category);
          }
        }
        if (categoryId) {
          categoryIds = [categoryId];
        }
      }

      // Normalize variantOptions to ensure all values arrays are properly initialized
      // IMPORTANT: Preserve translations for both options and values
      const normalizedVariantOptions = Array.isArray(productData.variantOptions)
        ? productData.variantOptions.map((option: any) => ({
            name: option.name || "",
            values: Array.isArray(option.values)
              ? option.values.map((value: any) => ({
                  value: value.value || "",
                  addOnPrice: value.addOnPrice ?? 0,
                  sku: value.sku || "",
                  image: value.image || "",
                  inventoryQuantity: value.inventoryQuantity,
                  isActive: value.isActive ?? true,
                  // Preserve translations for values
                  translations: value.translations || {},
                }))
              : [],
            required: option.required ?? false,
            displayOrder: option.displayOrder ?? 0,
            // Preserve translations for options
            translations: option.translations || {},
          }))
        : [];

      setFormData({
        ...productData,
        categories: categoryIds,
        // Ensure variantOptions is always an array with properly initialized values
        variantOptions: normalizedVariantOptions,
        // Ensure arrays are always initialized
        images: Array.isArray(productData.images) ? productData.images : [],
        tags: Array.isArray(productData.tags) ? productData.tags : [],
        metafields: Array.isArray(productData.metafields)
          ? productData.metafields
          : [],
        // Ensure digitalFiles are properly loaded with countryCodes
        digitalFiles: Array.isArray(productData.digitalFiles)
          ? productData.digitalFiles.map((file: any) => ({
              ...file,
              countryCodes: Array.isArray(file.countryCodes)
                ? file.countryCodes
                : undefined,
            }))
          : [],
        canvasSize:
          productData.canvasSize ||
          (productData.customizationType === "2d-print" ? "mug" : undefined),
        backgroundImages: Array.isArray(productData.backgroundImages)
          ? productData.backgroundImages
          : [],
        availableAssetIds: Array.isArray(productData.availableAssetIds)
          ? productData.availableAssetIds.map((id: any) =>
              typeof id === "string" ? id : id._id || id.toString(),
            )
          : [],
        allowedDesignerFonts: Array.isArray(productData.allowedDesignerFonts)
          ? productData.allowedDesignerFonts
          : [],
        availableColors: Array.isArray(productData.availableColors)
          ? productData.availableColors
          : [],
        // Keep inventoryQuantity as-is (don't default to 0 - undefined means not set)
        inventoryQuantity:
          productData.inventoryQuantity !== undefined &&
          productData.inventoryQuantity !== null
            ? productData.inventoryQuantity
            : undefined,
        // Ensure translations object exists
        translations: productData.translations || {},
        // Ensure locationPricing is properly loaded
        locationPricing: Array.isArray(productData.locationPricing)
          ? productData.locationPricing.map((lp: any) => ({
              locationId:
                typeof lp.locationId === "string"
                  ? lp.locationId
                  : lp.locationId?.toString() || lp.locationId,
              price: lp.price,
              inventoryQuantity: lp.inventoryQuantity,
              isAvailable: lp.isAvailable !== false,
              discount: lp.discount,
            }))
          : [],
        // Ensure variantCombinations locationPricing is properly loaded
        variantCombinations: Array.isArray(productData.variantCombinations)
          ? productData.variantCombinations.map((combo: any) => ({
              ...combo,
              locationPricing: Array.isArray(combo.locationPricing)
                ? combo.locationPricing.map((lp: any) => ({
                    locationId:
                      typeof lp.locationId === "string"
                        ? lp.locationId
                        : lp.locationId?.toString() || lp.locationId,
                    price: lp.price,
                    inventoryQuantity: lp.inventoryQuantity,
                    isAvailable: lp.isAvailable !== false,
                    discount: lp.discount,
                  }))
                : [],
              digitalFiles: Array.isArray(combo.digitalFiles)
                ? combo.digitalFiles
                : [],
            }))
          : productData.variantCombinations || [],
      });

      // Load profit settings
      setRootMaterialCost(productData.materialCost);
      const mappedAssignments = (productData.contributorAssignments || []).map(
        (a: any) => ({
          contributorId: a.contributorId?._id
            ? String(a.contributorId._id)
            : a.contributorId
              ? String(a.contributorId)
              : "",
          roleId: a.roleId?._id
            ? String(a.roleId._id)
            : a.roleId
              ? String(a.roleId)
              : "",
        }),
      );
      setContributorAssignments(mappedAssignments);

      // Load location material costs
      if (productData.locationPricing) {
        const locationCosts: Record<string, number | undefined> = {};
        productData.locationPricing.forEach((lp: any) => {
          if (lp.materialCost !== undefined) {
            locationCosts[lp.locationId] = lp.materialCost;
          }
        });
        setLocationMaterialCosts(locationCosts);
      }

      // Load variant option value material costs
      if (productData.variantOptions) {
        const optionValueCosts: Record<string, number | undefined> = {};
        productData.variantOptions.forEach((option: any, optIndex: number) => {
          option.values.forEach((value: any, valIndex: number) => {
            const key = `${optIndex}-${valIndex}`;
            if (value.materialCost !== undefined) {
              optionValueCosts[key] = value.materialCost;
            }
          });
        });
        setVariantOptionValueMaterialCosts(optionValueCosts);
      }

      // Load variant combination material costs
      if (productData.variantCombinations) {
        const combinationCosts: Record<string, number | undefined> = {};
        productData.variantCombinations.forEach((combo: any) => {
          if (combo.materialCost !== undefined) {
            combinationCosts[combo.combination] = combo.materialCost;
          }
        });
        setVariantCombinationMaterialCosts(combinationCosts);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t("admin.errors.fetchProduct"));
    } finally {
      setLoading(false);
    }
  }, [api, productId, t]);

  // Store fetch functions in refs to avoid dependency issues
  useEffect(() => {
    fetchCategoriesRef.current = fetchCategories;
    fetchProductRef.current = fetchProduct;
  }, [fetchCategories, fetchProduct]);

  // Reset form data immediately when modal opens or productId changes
  useEffect(() => {
    // Only run if isOpen or productId actually changed
    const isOpenChanged = prevIsOpenRef.current !== isOpen;
    const productIdChanged = prevProductIdRef.current !== productId;

    if (!isOpenChanged && !productIdChanged) {
      return;
    }

    // Update refs
    prevIsOpenRef.current = isOpen;
    prevProductIdRef.current = productId;

    if (isOpen) {
      // Reset form data immediately to prevent showing old data
      const resetFormData = {
        title: "",
        description: "",
        status: "draft" as const,
        basePrice: 0,
        variantOptions: [],
        images: [],
        tags: [],
        giftCard: false,
        featured: false,
        isCustomizable: false,
        isDigital: false,
        digitalFiles: [],
        metafields: [],
        canvasSize: undefined,
        backgroundImages: [],
        availableAssetIds: [],
        allowedDesignerFonts: [],
        availableColors: [],
        translations: {},
      };
      setFormData(resetFormData);
      setError(null);

      // Reset profit settings
      setRootMaterialCost(undefined);
      setLocationMaterialCosts({});
      setVariantOptionValueMaterialCosts({});
      setVariantCombinationMaterialCosts({});
      setContributorAssignments([]);

      // Set loading state based on whether we're editing
      if (productId) {
        setLoading(true);
      } else {
        setLoading(false);
      }

      // Fetch categories first, then product (if editing)
      // Use refs to avoid dependency issues
      const fetchCategoriesFn = fetchCategoriesRef.current;
      const fetchProductFn = fetchProductRef.current;

      if (fetchCategoriesFn) {
        fetchCategoriesFn().then(() => {
          if (productId && fetchProductFn) {
            fetchProductFn();
          }
        });
      }
    } else {
      // Reset state when modal closes
      setLoading(false);
      setError(null);
    }
  }, [isOpen, productId]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      try {
        setSaving(true);
        setError(null);

        const payload: any = {
          ...formData,
          tags: formData.tags?.length ? formData.tags : [],
          categories: formData.categories || [],
          materialCost: rootMaterialCost,
          contributorAssignments: contributorAssignments.filter(
            (a) => a.contributorId && a.roleId,
          ),
        };

        // Only include inventoryQuantity if product has no variantOptions
        // (inventory for products with variantOptions is managed at the variant level)
        if (!formData.variantOptions || formData.variantOptions.length === 0) {
          // Only include inventoryQuantity if it's explicitly set (not undefined)
          // If undefined, don't include it in the payload (let it remain unset in DB)
          if (
            formData.inventoryQuantity !== undefined &&
            formData.inventoryQuantity !== null
          ) {
            payload.inventoryQuantity = formData.inventoryQuantity;
          } else {
            // Remove it from payload if not set
            delete payload.inventoryQuantity;
          }
          // Explicitly clear variantCombinations when all variants are removed
          payload.variantCombinations = [];
        } else {
          // Remove inventoryQuantity if variantOptions exist (inventory is managed by variants)
          delete payload.inventoryQuantity;
        }

        // Remove handle from payload - server will auto-generate it
        delete payload.handle;

        // Add material costs to location pricing
        if (formData.locationPricing && formData.locationPricing.length > 0) {
          payload.locationPricing = formData.locationPricing.map((lp: any) => ({
            ...lp,
            materialCost: locationMaterialCosts[lp.locationId] || undefined,
          }));
        }

        // Note: Variants are generated from variantOptions, so material costs are handled at variantCombination level

        // Add material costs to variant options
        if (formData.variantOptions && formData.variantOptions.length > 0) {
          payload.variantOptions = formData.variantOptions.map(
            (option: any, optIndex: number) => ({
              ...option,
              values: option.values.map((value: any, valIndex: number) => {
                const key = `${optIndex}-${valIndex}`;
                return {
                  ...value,
                  materialCost:
                    variantOptionValueMaterialCosts[key] || undefined,
                };
              }),
            }),
          );
        }

        // Add material costs to variant combinations
        // Only process variantCombinations if variantOptions exist (variants are managed)
        if (
          formData.variantOptions &&
          formData.variantOptions.length > 0 &&
          formData.variantCombinations &&
          formData.variantCombinations.length > 0
        ) {
          payload.variantCombinations = formData.variantCombinations.map(
            (combo: any) => ({
              ...combo,
              materialCost:
                variantCombinationMaterialCosts[combo.combination] || undefined,
              digitalFiles: combo.digitalFiles || undefined, // Preserve variant-level digital files
            }),
          );
        }

        if (productId) {
          await api.patch(`/products/${productId}`, payload);
        } else {
          await api.post("/products", payload);
        }

        onSuccess();
        onClose();
      } catch (err: any) {
        setError(err.response?.data?.error || t("admin.errors.saveProduct"));
      } finally {
        setSaving(false);
      }
    },
    [
      formData,
      productId,
      api,
      onSuccess,
      onClose,
      t,
      rootMaterialCost,
      contributorAssignments,
      locationMaterialCosts,
      variantOptionValueMaterialCosts,
      variantCombinationMaterialCosts,
    ],
  );

  const handleImageUpload = useCallback(
    (url: string, type?: "image" | "video") => {
      setFormData((prev) => {
        const newImages = [...(prev.images || [])];
        const isVideo =
          type === "video" ||
          url.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i) !== null;
        newImages.push({
          url,
          isPrimary: newImages.length === 0 && !isVideo, // Only images can be primary
          type: isVideo ? "video" : "image",
        });
        return { ...prev, images: newImages };
      });
    },
    [],
  );

  const handleImageRemove = useCallback((index: number) => {
    setFormData((prev) => {
      const newImages = (prev.images || []).filter((_, i) => i !== index);
      return { ...prev, images: newImages };
    });
  }, []);

  const handleImageSetPrimary = useCallback((index: number) => {
    setFormData((prev) => {
      const images = prev.images || [];
      if (index < 0 || index >= images.length) {
        return prev;
      }

      const selectedMedia = images[index];
      // Only images can be primary, not videos
      if (
        selectedMedia.type === "video" ||
        (!selectedMedia.type &&
          selectedMedia.url
            .toLowerCase()
            .match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i))
      ) {
        return prev;
      }

      // Move the selected image to the first position
      const newImages = [...images];
      const selectedImage = { ...newImages[index], isPrimary: true };
      newImages.splice(index, 1);
      newImages.unshift(selectedImage);

      // Remove isPrimary flag from all other images
      const updatedImages = newImages.map((img, i) => ({
        ...img,
        isPrimary:
          i === 0 &&
          img.type !== "video" &&
          (!img.type ||
            !img.url.toLowerCase().match(/\.(mp4|webm|ogg|mov|avi|mkv)$/i)),
      }));

      // Update variant combination image indices to account for the reordering
      const updatedVariantCombinations =
        prev.variantCombinations?.map((combo: any) => {
          if (combo.imageIndex === undefined || combo.imageIndex === null) {
            return combo;
          }

          const oldIndex = combo.imageIndex;
          let newIndex: number;

          if (oldIndex === index) {
            // The image that was moved is now at index 0
            newIndex = 0;
          } else if (oldIndex < index) {
            // Images before the moved one are shifted down by 1
            newIndex = oldIndex + 1;
          } else {
            // Images after the moved one remain unchanged
            newIndex = oldIndex;
          }

          return {
            ...combo,
            imageIndex: newIndex,
          };
        }) || prev.variantCombinations;

      return {
        ...prev,
        images: updatedImages,
        variantCombinations: updatedVariantCombinations,
      };
    });
  }, []);

  // Note: handleImageTranslationUpload is now created inline in ModalWithLanguageContent
  // where we have access to currentLanguage

  // Note: Variant combination management is now handled by VariantCombinationsTable component

  const addVariantOption = useCallback(() => {
    setFormData((prev) => {
      const newOptions = [...(prev.variantOptions || [])];
      newOptions.push({
        name: "",
        values: [],
        required: false,
        displayOrder: newOptions.length,
      });
      // Clear variant combinations when adding a new option (structure changes)
      return { ...prev, variantOptions: newOptions, variantCombinations: [] };
    });
  }, []);

  const removeVariantOption = useCallback((index: number) => {
    setFormData((prev) => {
      const currentOptions = prev.variantOptions || [];
      const newOptions = currentOptions.filter((_, i) => i !== index);
      const removedOption = currentOptions[index];

      // Clear variant combinations that reference the removed option
      const updatedCombinations = (prev.variantCombinations || []).filter(
        (combo: any) => {
          if (!removedOption || !removedOption.name) return true;
          const pairs = combo.combination.split("|");
          return !pairs.some((pair: string) => {
            const [optionName] = pair.split(":");
            return optionName === removedOption.name;
          });
        },
      );

      // If all variant options are removed, explicitly clear variant combinations and ensure variantOptions is empty array
      const finalCombinations =
        newOptions.length === 0 ? [] : updatedCombinations;
      const finalOptions = newOptions.length === 0 ? [] : newOptions;

      return {
        ...prev,
        variantOptions: finalOptions,
        variantCombinations: finalCombinations,
      };
    });
  }, []);

  // Note: addVariantOptionValue and removeVariantOptionValue are now handled by MultiValueInput

  const addMetafield = useCallback(() => {
    setFormData((prev) => {
      const newMetafields = [...(prev.metafields || [])];
      newMetafields.push({ key: "", value: "", namespace: "custom" });
      return { ...prev, metafields: newMetafields };
    });
  }, []);

  const removeMetafield = useCallback((index: number) => {
    setFormData((prev) => {
      const newMetafields = (prev.metafields || []).filter(
        (_, i) => i !== index,
      );
      return { ...prev, metafields: newMetafields };
    });
  }, []);

  return (
    <ModalLanguageProvider>
      <ModalWithLanguageContent
        isOpen={isOpen}
        onClose={onClose}
        productId={productId}
        saving={saving}
        loading={loading}
        formData={formData}
        setFormData={setFormData}
        error={error}
        categories={categories}
        t={t}
        handleSubmit={handleSubmit}
        handleImageUpload={handleImageUpload}
        handleImageRemove={handleImageRemove}
        handleImageSetPrimary={handleImageSetPrimary}
        addVariantOption={addVariantOption}
        removeVariantOption={removeVariantOption}
        addMetafield={addMetafield}
        removeMetafield={removeMetafield}
        api={api}
        contributors={contributors}
        roles={roles}
        rootMaterialCost={rootMaterialCost}
        setRootMaterialCost={setRootMaterialCost}
        locationMaterialCosts={locationMaterialCosts}
        setLocationMaterialCosts={setLocationMaterialCosts}
        variantCombinationMaterialCosts={variantCombinationMaterialCosts}
        setVariantCombinationMaterialCosts={setVariantCombinationMaterialCosts}
        contributorAssignments={contributorAssignments}
        setContributorAssignments={setContributorAssignments}
      />
    </ModalLanguageProvider>
  );
};

// Separate component for ModalWithLanguage to avoid recreation - defined outside to maintain stable reference
const ModalWithLanguageContent: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  productId?: string;
  saving: boolean;
  loading: boolean;
  formData: Partial<Product>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<Product>>>;
  error: string | null;
  categories: any[];
  t: (key: string) => string;
  handleSubmit: (e: React.FormEvent) => void;
  handleImageUpload: (url: string, type?: "image" | "video") => void;
  handleImageRemove: (index: number) => void;
  handleImageSetPrimary: (index: number) => void;
  addVariantOption: () => void;
  removeVariantOption: (index: number) => void;
  addMetafield: () => void;
  removeMetafield: (index: number) => void;
  api: any;
  contributors: any[];
  roles: any[];
  rootMaterialCost?: number;
  setRootMaterialCost: (value: number | undefined) => void;
  locationMaterialCosts: Record<string, number | undefined>;
  setLocationMaterialCosts: React.Dispatch<
    React.SetStateAction<Record<string, number | undefined>>
  >;
  variantCombinationMaterialCosts: Record<string, number | undefined>;
  setVariantCombinationMaterialCosts: React.Dispatch<
    React.SetStateAction<Record<string, number | undefined>>
  >;
  contributorAssignments: Array<{ contributorId: string; roleId: string }>;
  setContributorAssignments: React.Dispatch<
    React.SetStateAction<Array<{ contributorId: string; roleId: string }>>
  >;
}> = ({
  isOpen,
  onClose,
  productId,
  saving,
  loading,
  formData,
  setFormData,
  error,
  categories,
  t,
  handleSubmit,
  handleImageUpload,
  handleImageRemove,
  handleImageSetPrimary,
  addVariantOption,
  removeVariantOption,
  addMetafield,
  removeMetafield,
  api,
  contributors,
  roles,
  rootMaterialCost,
  setRootMaterialCost,
  locationMaterialCosts,
  setLocationMaterialCosts,
  variantCombinationMaterialCosts,
  setVariantCombinationMaterialCosts,
  contributorAssignments,
  setContributorAssignments,
}) => {
  const { currentLanguage, setCurrentLanguage } = useModalLanguage();
  const { isPluginEnabled } = useStore();
  const isDiscountsPluginEnabled = isPluginEnabled("discounts-promos");
  const [activeTab, setActiveTab] = useState<
    "basic" | "pricing" | "variants" | "discounts" | "advanced"
  >("basic");

  // Check if there are valid variant options
  const hasVariantOptions = useMemo(() => {
    if (
      !formData.variantOptions ||
      !Array.isArray(formData.variantOptions) ||
      formData.variantOptions.length === 0
    ) {
      return false;
    }
    // Check if there's at least one valid variant option (with name and at least one value)
    return formData.variantOptions.some((opt: any) => {
      if (!opt) return false;
      const hasName = opt.name && opt.name.trim() !== "";
      if (!hasName) return false;
      const hasValues =
        opt.values &&
        Array.isArray(opt.values) &&
        opt.values.length > 0 &&
        opt.values.some(
          (val: any) => val && val.value && val.value.trim() !== "",
        );
      return hasValues;
    });
  }, [formData.variantOptions]);

  // Redirect to basic tab if pricing tab is active but should be hidden
  useEffect(() => {
    if (activeTab === "pricing" && hasVariantOptions) {
      setActiveTab("basic");
    }
  }, [activeTab, hasVariantOptions]);

  const headerActionsMemo = useMemo(
    () => (
      <LanguageSwitcher
        currentLanguage={currentLanguage}
        onLanguageChange={setCurrentLanguage}
        showLabel={false}
      />
    ),
    [currentLanguage, setCurrentLanguage],
  );

  const headerContentMemo = useMemo(
    () => (
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-1 flex-1 overflow-x-auto scrollbar-hide">
          <button
            type="button"
            onClick={() => setActiveTab("basic")}
            className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "basic"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {t("admin.productForm.basicInformation")}
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("variants")}
            className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "variants"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {t("admin.productForm.variantOptions")} & {t("admin.images")}
          </button>
          {!hasVariantOptions && (
            <button
              type="button"
              onClick={() => setActiveTab("pricing")}
              className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "pricing"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t("admin.productForm.pricing")} &{" "}
              {t("admin.productForm.inventory")}
            </button>
          )}
          {isDiscountsPluginEnabled && (
            <button
              type="button"
              onClick={() => setActiveTab("discounts")}
              className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === "discounts"
                  ? "border-b-2 border-blue-500 text-blue-600"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              {t("admin.productForm.discounts") || "Discounts"}
            </button>
          )}
          <button
            type="button"
            onClick={() => setActiveTab("advanced")}
            className={`px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "advanced"
                ? "border-b-2 border-blue-500 text-blue-600"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {t("common.advanced") || "Advanced"}
          </button>
        </div>
        <div className="flex items-center gap-3 ml-4 flex-shrink-0">
          {headerActionsMemo}
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-store-secondary"
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    ),
    [activeTab, t, headerActionsMemo, onClose, hasVariantOptions],
  );

  const footerMemo = useMemo(
    () => (
      <div className="flex gap-2 justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={saving}
        >
          {t("common.cancel")}
        </Button>
        <Button type="submit" form="product-form" disabled={saving}>
          {saving ? (
            <Spinner size="sm" />
          ) : productId ? (
            t("admin.updateProduct")
          ) : (
            t("admin.addProduct")
          )}
        </Button>
      </div>
    ),
    [saving, productId, onClose, t],
  );

  const handleImageTranslationUpload = useCallback(
    (imageIndex: number, url: string) => {
      setFormData((prev) => {
        const newImages = [...(prev.images || [])];
        if (newImages[imageIndex]) {
          const translations = {
            ...(newImages[imageIndex].translations || {}),
          };
          translations[currentLanguage] = url;
          newImages[imageIndex] = {
            ...newImages[imageIndex],
            translations,
          };
        }
        return { ...prev, images: newImages };
      });
    },
    [currentLanguage, setFormData],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="fullscreen"
      headerContent={headerContentMemo}
      footer={footerMemo}
    >
      {loading ? (
        <div className="flex justify-center items-center min-h-[400px]">
          <Spinner size="lg" />
        </div>
      ) : (
        <MemoizedProductFormContent
          formData={formData}
          setFormData={setFormData}
          error={error}
          categories={categories}
          t={t}
          currentLanguage={currentLanguage}
          handleSubmit={handleSubmit}
          handleImageUpload={handleImageUpload}
          handleImageTranslationUpload={handleImageTranslationUpload}
          handleImageRemove={handleImageRemove}
          handleImageSetPrimary={handleImageSetPrimary}
          addVariantOption={addVariantOption}
          removeVariantOption={removeVariantOption}
          addMetafield={addMetafield}
          removeMetafield={removeMetafield}
          productId={productId}
          api={api}
          contributors={contributors}
          roles={roles}
          rootMaterialCost={rootMaterialCost}
          setRootMaterialCost={setRootMaterialCost}
          locationMaterialCosts={locationMaterialCosts}
          setLocationMaterialCosts={setLocationMaterialCosts}
          variantCombinationMaterialCosts={variantCombinationMaterialCosts}
          setVariantCombinationMaterialCosts={
            setVariantCombinationMaterialCosts
          }
          contributorAssignments={contributorAssignments}
          setContributorAssignments={setContributorAssignments}
          activeTab={activeTab}
        />
      )}
    </Modal>
  );
};
